/**
 * Admin → Dealer desk: the approval queue (shared with the CRM), staff and roles, and the audit log.
 * Requests come from Platform Admin or the CRM; the first decision wins, and nobody decides their own.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { fmtDateTime, fmtUsd } from '@platform/shared';
import { periodRangeOf, TimeFrameSelect, type Period } from '../components/DateRange';
import { InstrumentLabel } from '../components/InstrumentIcon';
import { cx, Empty, Modal, Spinner } from '../components/ui';
import { api, ApiError, downloadFile } from '../lib/api';
import { useLoad } from '../stores/account';

const th = 'px-3 py-2 text-left text-xs font-medium text-fg/70 whitespace-nowrap';
const td = 'px-3 py-2 align-top';
const input = 'h-9 rounded-lg border border-line bg-bg px-3 text-[13px] text-fg outline-none focus:border-line-strong placeholder:text-muted';
const btn = 'h-8 rounded-md border border-line-strong px-3 text-xs font-semibold text-fg disabled:opacity-50';
const primary = 'h-8 rounded-md bg-accent px-3 text-xs font-semibold text-white disabled:opacity-50';
const err = (e: unknown) => (e instanceof ApiError ? e.message : 'Something went wrong');

type Role = 'support' | 'dealer' | 'senior_dealer' | 'finance' | 'compliance' | 'admin' | 'retention_dealer';
const ROLE_LABEL: Record<Role, string> = {
  support: 'Support',
  dealer: 'Dealer',
  senior_dealer: 'Senior dealer',
  finance: 'Finance',
  compliance: 'Compliance',
  admin: 'Admin',
  retention_dealer: 'Retention dealer (own clients)',
};
/** Roles a CRM user holds, never a Platform login: a retention dealer works from the CRM (RETENTION_DEALER_PLAN.md). */
const CRM_ONLY: Role[] = ['retention_dealer'];
const rolesFor = (platformLogin: boolean) => (Object.keys(ROLE_LABEL) as Role[]).filter((r) => !platformLogin || !CRM_ONLY.includes(r));
const KIND_LABEL: Record<string, string> = {
  'money.deposit': 'Manual deposit',
  'money.withdraw': 'Manual withdrawal',
  'money.credit_in': 'Credit in',
  'money.credit_out': 'Credit out',
  'money.zero_balance': 'Zero balance',
  'money.rollover': 'Rollover adjustment',
  'position.correction': 'Trade correction',
  'trade.transfer': 'Transferred trade',
  'trade.edit': 'Trade edit',
  'trade.restore': 'Restore trade',
  'money.balance_transfer': 'Balance from previous broker',
  'account.terms': 'Account terms',
};

interface Staff {
  id: string;
  userId: string | null;
  crmActorId: string | null;
  name: string;
  role: Role;
  status: 'active' | 'disabled';
  email?: string | null;
}
interface PermissionRow {
  permission: string;
  act: 'none' | 'request' | 'direct';
  directLimit: string | null;
  approve: boolean;
  approveLimit: string | null;
}
interface Approval {
  id: string;
  number: string;
  kind: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled' | 'failed';
  targetUserId: string | null;
  targetAccountId: string | null;
  amount: string | null;
  preview: Record<string, any>;
  reason: string;
  requestedBy: { id: string; name: string | null };
  requestedVia: 'admin' | 'crm';
  requestedAt: string;
  decidedBy: { id: string; name: string | null } | null;
  decidedVia: 'admin' | 'crm' | null;
  decidedAt: string | null;
  decisionComment: string | null;
  executedAt: string | null;
  error: string | null;
}

function Table({ head, children, minWidth = 800 }: { head: string[]; children: ReactNode; minWidth?: number }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-line bg-panel">
      <table className="w-full border-collapse text-xs" style={{ minWidth }}>
        <thead className="border-b border-line">
          <tr>
            {head.map((h) => (
              <th key={h} className={th}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

const STATUS_TONE: Record<Approval['status'], string> = {
  pending: 'bg-warn/15 text-warn',
  approved: 'bg-up/15 text-up',
  rejected: 'bg-down/15 text-down',
  cancelled: 'bg-raised text-muted',
  failed: 'bg-down/15 text-down',
};
const Badge = ({ className, children }: { className: string; children: ReactNode }) => (
  <span className={cx('inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold', className)}>{children}</span>
);
const Via = ({ via }: { via: 'admin' | 'crm' | null }) =>
  via ? <Badge className={via === 'crm' ? 'bg-accent/10 text-accent' : 'bg-raised text-muted'}>{via === 'crm' ? 'CRM' : 'Platform'}</Badge> : null;

const flat = (o: Record<string, unknown> | undefined) => Object.entries(o ?? {}).map(([k, v]) => `${k}: ${String(v)}`).join(', ');
const changeOf = (b: Record<string, unknown> | undefined, a: Record<string, unknown> | undefined) => (flat(b) ? `${flat(b)} → ${flat(a)}` : flat(a));
/** A transferred trade's figures, before → after, as the approver checks them against the statement. */
function transferLines(p: Record<string, any>): string[] {
  const b = p.before ?? {};
  const a = p.after ?? {};
  const when = (x: string | null) => (x ? fmtDateTime(x) : '—');
  return [
    `Transferred trade on ${p.symbol}: balance ${fmtUsd(p.amount)}`,
    `Open ${b.openPrice} → ${a.openPrice} · opened ${when(b.openedAt)} → ${when(a.openedAt)}`,
    ...(a.closePrice ? [`Close ${b.closePrice} → ${a.closePrice} · closed ${when(b.closedAt)} → ${when(a.closedAt)}`] : []),
    `Swap ${b.swap} → ${a.swap} · commission ${b.commission} → ${a.commission}${a.realizedPnl ? ` · result ${fmtUsd(a.realizedPnl)}` : ''}`,
  ];
}

/** A trade edit: each changed figure, before → after. */
function editLines(p: Record<string, any>): string[] {
  const b = p.before ?? {};
  const a = p.after ?? {};
  const label: Record<string, string> = { side: 'Type', qty: 'Amount', leverage: 'Leverage', openPrice: 'Open price', openedAt: 'Open time', closePrice: 'Close price', closedAt: 'Close time', realizedPnl: 'Result' };
  const show = (k: string, v: any) => (v == null ? '—' : k === 'side' ? (v === 'long' ? 'Buy' : 'Sell') : k.endsWith('At') ? fmtDateTime(v) : k === 'realizedPnl' ? fmtUsd(v) : String(v));
  return [`Trade edit on ${p.symbol}: balance ${fmtUsd(p.amount)}`, ...((p.changed ?? []) as string[]).map((k) => `${label[k] ?? k} ${show(k, b[k])} → ${show(k, a[k])}`)];
}

const previewLines = (p: Record<string, any>) =>
  [
    ...(p.kind === 'transfer' && p.symbol ? transferLines(p) : []),
    ...(p.kind === 'edit' && p.symbol ? editLines(p) : []),
    p.kind === 'restore' && p.symbol &&
      `Restore ${p.side === 'long' ? 'Buy' : 'Sell'} ${p.symbol}${p.tradeId ? ` #${p.tradeId}` : ''} opened ${fmtDateTime(p.openedAt)} at ${p.openPrice}, closed ${fmtDateTime(p.closedAt)} at ${p.closePrice}: close taken back, ${fmtUsd(p.amount)} on the balance`,
    p.symbol && p.kind !== 'transfer' && p.kind !== 'edit' && p.kind !== 'restore' && `${p.kind} on ${p.symbol}: entry ${fmtUsd(p.amount)} (${changeOf(p.before, p.after)})`,
    p.balanceBefore !== undefined && `Balance ${fmtUsd(p.balanceBefore)} → ${fmtUsd(p.balanceAfter)}`,
    p.creditBefore !== undefined && `Credit ${fmtUsd(p.creditBefore)} → ${fmtUsd(p.creditAfter)}`,
    p.ticket && `Previous broker ${p.broker}, ticket ${p.ticket}`,
    p.checks && `Price check: open ${CHECK_LABEL(p.checks.open)}${p.checks.close ? `, close ${CHECK_LABEL(p.checks.close)}` : ''}`,
  ].filter(Boolean) as string[];
/** A transferred trade's price against our own candles at that time. */
const CHECK_LABEL = (c: { status: string; low: string | null; high: string | null; offByPct: string | null }) =>
  c.status === 'ok' ? `inside our market (${c.low} – ${c.high})` : c.status === 'outside' ? `⚠ ${c.offByPct}% outside our market (${c.low} – ${c.high})` : 'not checked (no market data)';

// ---------- approvals ----------

export function ApprovalsTab() {
  const [status, setStatus] = useState<'pending' | 'all'>('pending');
  const [mine, setMine] = useState(false);
  const me = useLoad<{ staff: Staff }>('/api/admin/desk/me');
  const { data, error, reload } = useLoad<Approval[]>(`/api/admin/approvals?status=${status}${mine ? '&mine=1' : ''}`);
  return (
    <div className="flex flex-col gap-3">
      <BulkRollover onDone={reload} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-lg font-semibold">Approvals</h2>
        <div className="flex flex-wrap gap-2">
          {(['pending', 'all'] as const).map((s) => (
            <button key={s} type="button" className={cx(btn, status === s && 'bg-raised')} onClick={() => setStatus(s)} aria-pressed={status === s}>
              {s === 'pending' ? 'Waiting' : 'All'}
            </button>
          ))}
          <button type="button" className={cx(btn, mine && 'bg-raised')} onClick={() => setMine(!mine)} aria-pressed={mine}>
            My requests
          </button>
        </div>
      </div>
      <p className="m-0 text-xs text-muted">
        One queue for Platform and the CRM. The first decision wins, in either screen. Nobody decides their own request.
        {me.data && ` You are ${me.data.staff.name} (${ROLE_LABEL[me.data.staff.role]}).`}
      </p>
      {error && <p className="m-0 text-xs text-down">{error}</p>}
      <div className="flex flex-col gap-2">
        {data?.map((a) => <ApprovalCard key={a.id} a={a} myId={me.data?.staff.id} onDone={reload} />)}
      </div>
      {data && !data.length && <Empty>{status === 'pending' ? 'Nothing is waiting for approval.' : 'No requests yet.'}</Empty>}
    </div>
  );
}

function ApprovalCard({ a, myId, onDone }: { a: Approval; myId?: string; onDone: () => void }) {
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const act = async (what: 'approve' | 'reject' | 'cancel') => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<Approval>(`/api/admin/approvals/${a.id}/${what}`, what === 'cancel' ? {} : { comment });
      if (r.status === 'failed') setMsg(`Approved, but it could not run: ${r.error}`);
      onDone();
    } catch (e) {
      setMsg(err(e));
    } finally {
      setBusy(false);
    }
  };
  const own = a.requestedBy.id === myId;
  return (
    <section className="flex flex-col gap-2 rounded-xl border border-line bg-panel p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-semibold">
            APR-{a.number} · {KIND_LABEL[a.kind] ?? a.kind}
            {typeof a.preview.symbol === 'string' && (
              <>
                {' · '}
                <InstrumentLabel symbol={a.preview.symbol} size={16} className="gap-1.5" />
              </>
            )}
            {a.amount && <span className="num"> · {fmtUsd(a.amount)}</span>}
          </span>
          <Via via={a.requestedVia} />
        </div>
        <Badge className={STATUS_TONE[a.status]}>{a.status}</Badge>
      </div>
      <div className="grid gap-1 text-xs text-muted sm:grid-cols-2">
        <span>
          Requested by <b className="text-fg">{a.requestedBy.name}</b> · {fmtDateTime(a.requestedAt)}
        </span>
        <span>Reason: <span className="text-fg">{a.reason}</span></span>
        {a.targetUserId && <span>Client: <span className="num text-fg">{a.targetUserId}</span></span>}
        {previewLines(a.preview).map((l) => (
          <span key={l} className="num">
            If approved: {l}
          </span>
        ))}
        {a.preview.evidence && <span className="sm:col-span-2">Evidence: <span className="text-fg">{a.preview.evidence}</span></span>}
        {a.decidedBy && (
          <span>
            {a.status === 'rejected' ? 'Rejected' : 'Decided'} by <b className="text-fg">{a.decidedBy.name}</b> <Via via={a.decidedVia} />
            {a.decisionComment && ` · “${a.decisionComment}”`}
          </span>
        )}
        {a.error && <span className="text-down">Could not run: {a.error}</span>}
      </div>
      {a.status === 'pending' && (
        <div className="flex flex-wrap items-center gap-2">
          {own ? (
            <button type="button" className={btn} disabled={busy} onClick={() => void act('cancel')}>
              {busy ? <Spinner /> : 'Withdraw my request'}
            </button>
          ) : (
            <>
              <input className={cx(input, 'min-w-60 flex-1')} placeholder="Comment (required to reject)" value={comment} onChange={(e) => setComment(e.target.value)} aria-label={`Comment on APR-${a.number}`} />
              <button type="button" className={primary} disabled={busy} onClick={() => void act('approve')}>
                {busy ? <Spinner /> : 'Approve and run'}
              </button>
              <button type="button" className={cx(btn, 'text-down')} disabled={busy || !comment.trim()} onClick={() => void act('reject')}>
                Reject
              </button>
            </>
          )}
          {msg && <span className="text-xs text-down">{msg}</span>}
        </div>
      )}
    </section>
  );
}

// ---------- staff and roles ----------

export function StaffTab() {
  const me = useLoad<{ staff: Staff }>('/api/admin/desk/me');
  const staff = useLoad<Staff[]>('/api/admin/staff');
  const tg = useLoad<TelegramColumn>('/api/admin/staff/telegram');
  const perms = useLoad<{ roles: Role[]; permissions: { key: string; label: string; money: boolean }[]; matrix: Record<string, PermissionRow[]> }>('/api/admin/staff/permissions');
  const [dialog, setDialog] = useState<Staff | 'new' | null>(null);
  const isAdmin = me.data?.staff.role === 'admin';
  const reload = () => {
    void staff.reload();
    void tg.reload();
  };
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-lg font-semibold">Staff &amp; roles</h2>
        {isAdmin && (
          <button type="button" className={primary} onClick={() => setDialog('new')}>
            Add staff member
          </button>
        )}
      </div>
      <p className="m-0 text-xs text-muted">
        One staff member per person, whichever screen they use: a Platform Admin login, a CRM user, or both. Limits and approvals follow the person.
      </p>
      {tg.data && <TelegramSummary t={tg.data} />}
      {staff.error && <p className="m-0 text-xs text-down">{staff.error}</p>}
      <Table head={['Name', 'Role', 'Platform login', 'CRM user', 'Second factor', 'Status', '']} minWidth={900}>
        {staff.data?.map((s) => (
          <StaffRow key={s.id} s={s} tg={tg.data?.data[s.id]} editable={isAdmin && s.id !== me.data?.staff.id} onEdit={() => setDialog(s)} onDone={reload} />
        ))}
      </Table>
      {dialog && (
        <StaffDialog
          staff={dialog === 'new' ? null : dialog}
          authenticator={dialog !== 'new' && !!tg.data?.data[dialog.id]?.authenticator}
          onClose={() => setDialog(null)}
          onDone={reload}
        />
      )}
      {perms.data && <RolePermissions data={perms.data} editable={isAdmin} onDone={perms.reload} />}
      <OfficeIps />
    </div>
  );
}

interface TelegramColumn {
  mode: 'off' | 'linked' | 'required';
  reachable: boolean;
  notSetUp: number;
  data: Record<
    string,
    {
      state: 'exempt' | 'no_crm_user' | 'linked' | 'blocked' | 'not_linked' | 'unknown';
      username?: string;
      authenticator: boolean;
      preferred: 'telegram' | 'authenticator' | null;
    }
  >;
}

const TG_MODE: Record<TelegramColumn['mode'], [string, string]> = {
  off: ['Off', 'bg-raised text-muted'],
  linked: ['Linked people only', 'bg-warn/15 text-warn'],
  required: ['Required for everyone', 'bg-up/15 text-up'],
};

/** The switch (STAFF_TG2FA on the server) and how many Platform logins can't get a code yet. */
function TelegramSummary({ t }: { t: TelegramColumn }) {
  const [label, cls] = TG_MODE[t.mode];
  return (
    <p className="m-0 flex flex-wrap items-center gap-2 text-xs text-muted">
      Second factor: <Badge className={cls}>{label}</Badge>
      {t.reachable ? (
        <span>{t.notSetUp ? `${t.notSetUp} ${t.notSetUp === 1 ? 'has' : 'have'} none` : 'everyone has one'}</span>
      ) : (
        <span className="text-down">The CRM didn’t answer, so Telegram links can’t be shown.</span>
      )}
    </p>
  );
}

/** One Second factor cell: Telegram (from the CRM) and the authenticator app, which comes first, or what fixes it. */
function TelegramCell({ s, t }: { s: Staff; t?: TelegramColumn['data'][string] }) {
  if (!s.userId || s.status !== 'active') return <span className="text-muted">—</span>;
  if (!t) return null;
  const hint = (txt: string) => <span className="ml-1 text-muted">· {txt}</span>;
  if (t.state === 'exempt') return <Badge className="bg-raised text-muted">Exempt</Badge>;
  const tg = t.state === 'linked';
  const both = tg && t.authenticator;
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      {tg && <Badge className="bg-up/15 text-up">{t.username ? `Telegram @${t.username}` : 'Telegram'}</Badge>}
      {t.authenticator && <Badge className="bg-up/15 text-up">Authenticator</Badge>}
      {both && hint(`uses ${t.preferred === 'authenticator' ? 'the app' : 'Telegram'} first`)}
      {!tg && !t.authenticator && (
        <>
          <Badge className="bg-down/15 text-down">None</Badge>
          {hint(t.state === 'unknown' ? 'CRM not reachable' : t.state === 'blocked' ? 'Telegram bot blocked, or set up an app' : 'link Telegram or set up an app')}
        </>
      )}
      {!tg && t.authenticator && t.state === 'blocked' && hint('Telegram bot blocked')}
    </span>
  );
}

interface StaffIpList {
  configured: boolean;
  switched_off_here: boolean;
  version: number;
  mode: 'report' | 'enforce' | null;
  open_until: string | null;
  received_at: string | null;
  received_from: string | null;
  entries: { address: string; label: string }[];
}

/**
 * The office addresses staff (every role but client, and "log in as client" sessions) may sign in
 * from. Read-only: the list is managed in the CRM (Settings → IP whitelist) and sent here. Admins only;
 * for anyone else the API answers 403 and nothing is shown.
 */
function OfficeIps() {
  const l = useLoad<StaffIpList>('/api/admin/staff-ip-allowlist');
  if (!l.data) return null;
  const d = l.data;
  const open = d.open_until && new Date(d.open_until).getTime() > Date.now();
  return (
    <section className="flex flex-col gap-2">
      <h3 className="m-0 text-base font-semibold">Office IPs</h3>
      {d.switched_off_here && (
        <p className="m-0 rounded-lg bg-down/10 px-3 py-2 text-xs text-down">The office IP check is switched off on this server (STAFF_IP_ALLOWLIST=off).</p>
      )}
      {!d.configured ? (
        <p className="m-0 text-xs text-muted">The CRM has not sent its IP whitelist yet. Until it does, staff can sign in from any address.</p>
      ) : (
        <>
          <p className="m-0 text-xs text-muted">
            Managed in the CRM (Settings → IP whitelist) · version {d.version}
            {d.received_at && ` · received ${fmtDateTime(d.received_at)}`}
            {d.received_from && ` from ${d.received_from}`} · mode{' '}
            <b className="text-fg">{d.mode === 'enforce' ? 'Enforced' : 'Report only'}</b>
            {open && <> · open to everyone until {fmtDateTime(d.open_until!)}</>}
          </p>
          <Table head={['Address', 'Label']} minWidth={360}>
            {d.entries.map((e) => (
              <tr key={e.address} className="border-b border-line last:border-0">
                <td className={cx(td, 'font-mono')}>{e.address}</td>
                <td className={td}>{e.label || '—'}</td>
              </tr>
            ))}
          </Table>
          <p className="m-0 text-xs text-muted">
            Applies to every account whose role is not client, and to sessions opened from the CRM with "Log in as client".
            {d.mode === 'report' && ' Report only: sign-ins from other addresses are recorded, not refused.'}
          </p>
        </>
      )}
    </section>
  );
}

interface CrmUser {
  id: string;
  name: string;
  email: string;
  role: string;
  telegram: { state: string; username: string };
  row: { id: string; name: string; platformLogin: boolean } | null;
}

/**
 * Add or edit a staff member: name, role, Platform login and CRM user. The CRM user is picked from
 * the CRM's people; when the CRM can't be reached the id can still be typed. Linking a CRM user or
 * login that already has a row of its own asks first, then merges the two rows into one.
 */
function StaffDialog({ staff, authenticator, onClose, onDone }: { staff: Staff | null; authenticator: boolean; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState({
    name: staff?.name ?? '',
    role: (staff?.role ?? 'dealer') as Role,
    email: staff?.email ?? '',
    crmActorId: staff?.crmActorId ?? '',
  });
  const [crmLabel, setCrmLabel] = useState<string>(staff?.crmActorId ? `CRM user #${staff.crmActorId}` : '');
  const [merge, setMerge] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const save = async (withMerge = false) => {
    setBusy(true);
    setMsg(null);
    try {
      if (staff) {
        await api.patch(`/api/admin/staff/${staff.id}`, {
          name: f.name,
          role: f.role,
          email: f.email.trim() || null,
          crmActorId: f.crmActorId.trim() || null,
          ...(withMerge ? { merge: true } : {}),
        });
      } else {
        await api.post('/api/admin/staff', { ...f, email: f.email.trim() || undefined, crmActorId: f.crmActorId.trim() || undefined });
      }
      onDone();
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'merge_needed') setMerge(e.message);
      else setMsg(err(e));
    } finally {
      setBusy(false);
    }
  };
  const ok = !!f.name.trim() && (!!f.email.trim() || !!f.crmActorId.trim());
  return (
    <Modal
      open
      onClose={onClose}
      title={staff ? `Edit ${staff.name}` : 'Add staff member'}
      width={520}
      footer={
        <div className="flex flex-wrap items-center justify-end gap-2">
          {msg && <span className="mr-auto text-xs text-down">{msg}</span>}
          <button type="button" className={btn} onClick={onClose}>
            Cancel
          </button>
          <button type="button" className={primary} disabled={busy || !ok} onClick={() => void save(!!merge)}>
            {busy ? <Spinner /> : merge ? 'Save and merge' : staff ? 'Save' : 'Add staff member'}
          </button>
        </div>
      }
    >
      <div className="flex flex-col gap-3 text-[13px]">
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">Name (shown on every staff screen)</span>
          <input className={input} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">Role</span>
          <select className={input} value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as Role })}>
            {rolesFor(!!f.email.trim()).map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs text-muted">Platform login (email of an admin or dealer account; leave empty for none)</span>
          <input
            className={input}
            value={f.email}
            onChange={(e) => {
              setMerge(null);
              setF({ ...f, email: e.target.value });
            }}
          />
        </label>
        <div className="flex flex-col gap-1">
          <span className="text-xs text-muted">CRM user</span>
          <CrmUserPicker
            value={f.crmActorId}
            label={crmLabel}
            ownRow={staff?.id ?? null}
            onPick={(id, label) => {
              setMerge(null);
              setCrmLabel(label);
              setF({ ...f, crmActorId: id });
            }}
          />
        </div>
        {staff?.userId && authenticator && <ResetAuthenticator staff={staff} onDone={onDone} />}
        {merge && <p className="m-0 rounded-lg bg-warn/10 px-3 py-2 text-xs text-warn">{merge} Their approvals and corrections move with it; the audit log keeps what was recorded.</p>}
      </div>
    </Modal>
  );
}

/** A lost phone: the authenticator app, its backup codes and the choice go; they sign in with Telegram or set it up again. */
function ResetAuthenticator({ staff, onDone }: { staff: Staff; onDone: () => void }) {
  const [state, setState] = useState<'idle' | 'confirm' | 'busy' | 'done'>('idle');
  const [msg, setMsg] = useState<string | null>(null);
  const reset = async () => {
    setState('busy');
    setMsg(null);
    try {
      await api.post(`/api/admin/staff/${staff.id}/reset-authenticator`);
      setState('done');
      onDone();
    } catch (e) {
      setMsg(err(e));
      setState('confirm');
    }
  };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-line p-3">
      <span className="text-xs text-muted">Authenticator app</span>
      {state === 'done' ? (
        <span className="text-xs text-up">Reset. {staff.name} signs in with Telegram, or sets the app up again on My sign-in security.</span>
      ) : state === 'idle' ? (
        <button type="button" className={cx(btn, 'self-start')} onClick={() => setState('confirm')}>
          Reset authenticator
        </button>
      ) : (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs">Remove {staff.name}’s authenticator app and backup codes? This is recorded in the audit log.</span>
          <button type="button" className={cx(btn, 'text-down')} disabled={state === 'busy'} onClick={() => void reset()}>
            {state === 'busy' ? <Spinner /> : 'Reset'}
          </button>
          <button type="button" className={btn} onClick={() => setState('idle')}>
            Cancel
          </button>
        </div>
      )}
      {msg && <span className="text-xs text-down">{msg}</span>}
    </div>
  );
}

const TG_STATE: Record<string, [string, string]> = {
  on: ['Telegram linked', 'bg-up/15 text-up'],
  blocked: ['Bot blocked', 'bg-down/15 text-down'],
};

/** Type a name or email, pick the CRM user. Falls back to typing the id when the CRM can't be reached. */
function CrmUserPicker({ value, label, ownRow, onPick }: { value: string; label: string; ownRow: string | null; onPick: (id: string, label: string) => void }) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState(false);
  // Ask the CRM once typing pauses, not on every key.
  const [asked, setAsked] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setAsked(q.trim()), 250);
    return () => clearTimeout(t);
  }, [q]);
  const found = useLoad<{ data: CrmUser[] }>(open && !manual ? `/api/admin/staff/crm-users?q=${encodeURIComponent(asked)}` : null, [asked, open, manual]);
  if (manual || (found.error && open)) {
    return (
      <div className="flex flex-col gap-1">
        <input className={input} placeholder="CRM user id, e.g. 27" value={value} onChange={(e) => onPick(e.target.value.replace(/\D/g, ''), '')} />
        {found.error && !manual && <span className="text-xs text-down">{found.error}</span>}
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-2">
        <input
          className={cx(input, 'flex-1')}
          placeholder={value ? label || `CRM user #${value}` : 'Search the CRM by name or email'}
          value={q}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
        />
        {value && (
          <button type="button" className={btn} onClick={() => onPick('', '')}>
            Remove
          </button>
        )}
      </div>
      {value && !open && <span className="text-xs text-muted">Linked: {label || `CRM user #${value}`}</span>}
      {open && (
        <div className="max-h-64 overflow-y-auto rounded-lg border border-line bg-raised" role="listbox">
          {found.loading && !found.data && <div className="px-3 py-2 text-xs text-muted">Searching the CRM…</div>}
          {found.data?.data.length === 0 && <div className="px-3 py-2 text-xs text-muted">Nobody in the CRM matches.</div>}
          {found.data?.data.map((u) => {
            // On another row that also has a login: taken. A CRM-only row can be merged into this one.
            const taken = !!u.row && u.row.id !== ownRow && u.row.platformLogin;
            const tg = TG_STATE[u.telegram.state] ?? ['No Telegram', 'bg-warn/15 text-warn'];
            return (
              <button
                key={u.id}
                type="button"
                role="option"
                aria-selected={u.id === value}
                disabled={taken}
                className={cx('flex w-full flex-wrap items-center justify-between gap-2 border-b border-line px-3 py-2 text-left last:border-0 disabled:opacity-50', u.id === value && 'bg-accent/10')}
                onClick={() => {
                  onPick(u.id, `${u.name} · #${u.id}`);
                  setQ('');
                  setOpen(false);
                }}
              >
                <span className="min-w-0">
                  <span className="block font-medium">
                    {u.name} · #{u.id}
                  </span>
                  <span className="block text-[11px] text-muted">
                    {taken ? `Already linked to ${u.row!.name} on this list` : [u.email, u.role && `${u.role} in the CRM`].filter(Boolean).join(' · ')}
                  </span>
                </span>
                {taken ? <Badge className="bg-raised text-muted">Taken</Badge> : <Badge className={tg[1]}>{tg[0]}</Badge>}
              </button>
            );
          })}
        </div>
      )}
      <button type="button" className="self-start text-[11px] text-muted hover:text-fg hover:underline" onClick={() => setManual(true)}>
        Type the id instead
      </button>
    </div>
  );
}

function StaffRow({ s, tg, editable, onEdit, onDone }: { s: Staff; tg?: TelegramColumn['data'][string]; editable: boolean; onEdit: () => void; onDone: () => void }) {
  const [msg, setMsg] = useState<string | null>(null);
  const patch = async (body: Partial<Staff>) => {
    setMsg(null);
    try {
      await api.patch(`/api/admin/staff/${s.id}`, body);
      onDone();
    } catch (e) {
      setMsg(err(e));
    }
  };
  return (
    <tr className="border-b border-line">
      <td className={td}>{s.name}</td>
      <td className={td}>
        {editable ? (
          <select className={cx(input, 'h-8')} value={s.role} onChange={(e) => void patch({ role: e.target.value as Role })} aria-label={`Role of ${s.name}`}>
            {rolesFor(!!s.userId).map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        ) : (
          ROLE_LABEL[s.role]
        )}
      </td>
      <td className={cx(td, 'text-muted')}>{s.email ?? '—'}</td>
      <td className={cx(td, 'num text-muted')}>{s.crmActorId ? `#${s.crmActorId}` : '—'}</td>
      <td className={td}>
        <TelegramCell s={s} t={tg} />
      </td>
      <td className={td}>
        <Badge className={s.status === 'active' ? 'bg-up/15 text-up' : 'bg-raised text-muted'}>{s.status}</Badge>
      </td>
      <td className={cx(td, 'whitespace-nowrap')}>
        {editable && (
          <span className="inline-flex gap-1.5">
            <button type="button" className={btn} onClick={onEdit} aria-label={`Edit ${s.name}`}>
              Edit
            </button>
            <button type="button" className={btn} onClick={() => void patch({ status: s.status === 'active' ? 'disabled' : 'active' })}>
              {s.status === 'active' ? 'Disable' : 'Enable'}
            </button>
          </span>
        )}
        {msg && <span className="ml-2 text-xs text-down">{msg}</span>}
      </td>
    </tr>
  );
}

type PermissionsData = { roles: Role[]; permissions: { key: string; label: string; money: boolean }[]; matrix: Record<string, PermissionRow[]> };

const ACTS: { act: PermissionRow['act']; label: string; hint: string; on: string }[] = [
  { act: 'none', label: 'None', hint: 'Not allowed', on: 'bg-raised text-fg' },
  { act: 'request', label: 'Request', hint: 'Asks for approval', on: 'bg-warn/15 text-warn' },
  { act: 'direct', label: 'Direct', hint: 'Does it at once, up to the limit (USD), and asks above it', on: 'bg-up/15 text-up' },
];

/** What each role may do: one role at a time, one readable row per permission. Admin may do everything, so it has no tab. */
function RolePermissions({ data, editable, onDone }: { data: PermissionsData; editable: boolean; onDone: () => void }) {
  const roles = data.roles.filter((r) => r !== 'admin') as Role[];
  const [picked, setRole] = useState<Role>(roles[0]!);
  const role = roles.includes(picked) ? picked : roles[0]!;
  return (
    <section className="flex flex-col gap-3">
      <h3 className="m-0 text-base font-semibold">What each role may do</h3>
      <ul className="m-0 flex list-none flex-col gap-1.5 p-0 text-[13px] text-fg/80">
        {ACTS.filter((a) => a.act !== 'none').map((a) => (
          <li key={a.act} className="flex items-baseline gap-2">
            <Badge className={a.on}>{a.label}</Badge> {a.hint}.
          </li>
        ))}
        <li className="flex items-baseline gap-2">
          <Badge className="bg-accent/15 text-accent-fg">Approve</Badge> May decide other people&apos;s requests, up to the limit.
        </li>
        <li className="text-muted">Admin may do and approve everything. An empty limit means no limit.</li>
      </ul>
      <div role="tablist" aria-label="Role" className="flex flex-wrap gap-x-5 border-b border-line">
        {roles.map((r) => (
          <button
            key={r}
            type="button"
            role="tab"
            aria-selected={role === r}
            className={cx('-mb-px border-b-2 px-1 py-2.5 text-sm font-semibold', role === r ? 'border-accent text-fg' : 'border-transparent text-muted hover:text-fg')}
            onClick={() => setRole(r)}
          >
            {ROLE_LABEL[r]}
          </button>
        ))}
      </div>
      <div role="tabpanel" aria-label={ROLE_LABEL[role]} className="divide-y divide-line rounded-xl border border-line bg-panel">
        {data.permissions.map((p) => (
          <PermissionRowEditor
            key={`${role}:${p.key}`}
            role={role}
            label={p.label}
            row={data.matrix[role]!.find((x) => x.permission === p.key)!}
            money={p.money}
            editable={editable}
            onDone={onDone}
          />
        ))}
      </div>
    </section>
  );
}

function LimitField({ label, value, onChange }: { label: string; value: string | null; onChange: (v: string | null) => void }) {
  return (
    <label className="flex flex-col gap-1 text-xs font-medium text-fg/80">
      {label}
      <input className={cx(input, 'num w-40')} inputMode="decimal" placeholder="No limit" value={value ?? ''} onChange={(e) => onChange(e.target.value || null)} />
    </label>
  );
}

function PermissionRowEditor({ role, label, row, money, editable, onDone }: { role: Role; label: string; row: PermissionRow; money: boolean; editable: boolean; onDone: () => void }) {
  const [f, setF] = useState(row);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const dirty = JSON.stringify(f) !== JSON.stringify(row);
  const save = async () => {
    setBusy(true);
    setMsg(null);
    try {
      await api.put('/api/admin/staff/permissions', { role, permission: row.permission, act: f.act, directLimit: f.directLimit, approve: f.approve, approveLimit: f.approveLimit });
      onDone();
    } catch (e) {
      setMsg(err(e));
    } finally {
      setBusy(false);
    }
  };
  const act = ACTS.find((a) => a.act === row.act)!;
  const title = (
    <div className="min-w-0 flex-1">
      <div className="text-sm font-medium text-fg">{label}</div>
      {money && <div className="mt-0.5 text-xs text-muted">Amounts in USD</div>}
    </div>
  );
  if (!editable) {
    return (
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3">
        {title}
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={act.on}>
            {act.label}
            {row.act === 'direct' && row.directLimit ? ` up to ${fmtUsd(row.directLimit)}` : ''}
          </Badge>
          {row.approve && <Badge className="bg-accent/15 text-accent-fg">Approve{row.approveLimit ? ` up to ${fmtUsd(row.approveLimit)}` : ''}</Badge>}
        </div>
      </div>
    );
  }
  return (
    <div className="flex flex-wrap items-start gap-x-6 gap-y-3 px-4 py-3">
      {title}
      <div className="flex flex-wrap items-end gap-x-5 gap-y-3">
        <div role="radiogroup" aria-label={`${ROLE_LABEL[role]}: ${label}`} className="inline-flex h-9 rounded-lg border border-line bg-bg p-0.5">
          {ACTS.map((a) => (
            <button
              key={a.act}
              type="button"
              role="radio"
              aria-checked={f.act === a.act}
              title={a.hint}
              onClick={() => setF({ ...f, act: a.act })}
              className={cx('rounded-md px-3 text-[13px] font-semibold transition-colors', f.act === a.act ? a.on : 'text-muted hover:text-fg')}
            >
              {a.label}
            </button>
          ))}
        </div>
        {money && f.act === 'direct' && <LimitField label="Direct up to (USD)" value={f.directLimit} onChange={(v) => setF({ ...f, directLimit: v })} />}
        <label className="flex h-9 cursor-pointer items-center gap-2 text-[13px] font-medium text-fg">
          <input type="checkbox" className="size-4 accent-accent" checked={f.approve} onChange={(e) => setF({ ...f, approve: e.target.checked })} />
          Can approve others
        </label>
        {money && f.approve && <LimitField label="Approve up to (USD)" value={f.approveLimit} onChange={(v) => setF({ ...f, approveLimit: v })} />}
        {dirty && (
          <div className="flex h-9 items-center gap-2">
            <button type="button" className={cx(primary, 'h-9 px-4 text-[13px]')} disabled={busy} onClick={() => void save()}>
              {busy ? <Spinner /> : 'Save'}
            </button>
            <button type="button" className={cx(btn, 'h-9 text-[13px]')} disabled={busy} onClick={() => setF(row)}>
              Undo
            </button>
          </div>
        )}
      </div>
      {msg && <p className="m-0 w-full text-xs text-down">{msg}</p>}
    </div>
  );
}

// ---------- audit log ----------

interface AuditRow {
  id: string;
  at: string;
  actor: { staffId: string | null; name: string | null; role: Role | null };
  channel: 'admin' | 'crm' | null;
  action: string;
  targetType: string;
  targetId: string;
  targetUserId: string | null;
  reason: string | null;
  approvalId: string | null;
  before: unknown;
  after: unknown;
}

const show = (v: unknown) => (v === null || v === undefined ? '—' : typeof v === 'object' ? JSON.stringify(v) : String(v));
const HIDDEN_KEYS = new Set(['id', 'userId', 'permission']);

/** "balance: 0 → 1000" for each field that changed; for a new record, its fields. */
function changeLines(before: unknown, after: unknown): string[] {
  const obj = (v: unknown) => (v && typeof v === 'object' ? (v as Record<string, unknown>) : null);
  const [b, a] = [obj(before), obj(after)];
  if (!a) return [];
  if (!b || !Object.keys(b).length) return Object.entries(a).filter(([k, v]) => !HIDDEN_KEYS.has(k) && v !== null).map(([k, v]) => `${k}: ${show(v)}`);
  return Object.keys({ ...b, ...a })
    .filter((k) => !HIDDEN_KEYS.has(k) && show(b[k]) !== show(a[k]))
    .map((k) => `${k}: ${show(b[k])} → ${show(a[k])}`);
}

const AUDIT_FILTERS = { userId: '', staffId: '', action: '', from: '', to: '' };

/** Audit days are UTC, both ends inclusive. */
const AUDIT_PERIODS: { id: number; label: string }[] = [
  { id: 0, label: 'All time' },
  { id: 1, label: 'Today (UTC)' },
  { id: 7, label: '7 days' },
  { id: 30, label: '30 days' },
  { id: 90, label: '90 days' },
];

export function AuditTab() {
  const [f, setF] = useState(AUDIT_FILTERS);
  const [period, setPeriod] = useState<Period<number>>({ id: 0 });
  const [applied, setApplied] = useState(AUDIT_FILTERS);
  const [exporting, setExporting] = useState(false);
  const [exportErr, setExportErr] = useState<string | null>(null);
  const staff = useLoad<Staff[]>('/api/admin/staff');
  const qs = new URLSearchParams(Object.entries(applied).filter(([, v]) => v.trim()) as [string, string][]).toString();
  const { data, error } = useLoad<AuditRow[]>(`/api/admin/audit${qs ? `?${qs}` : ''}`);
  const exportCsv = async () => {
    setExporting(true);
    setExportErr(null);
    try {
      await downloadFile(`/api/admin/audit.csv${qs ? `?${qs}` : ''}`, `audit-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e) {
      setExportErr(err(e));
    } finally {
      setExporting(false);
    }
  };
  return (
    <div className="flex flex-col gap-3">
      <h2 className="m-0 text-lg font-semibold">Audit log</h2>
      <p className="m-0 text-xs text-muted">Every staff action, newest first. Nothing here can be changed or deleted. Exports are themselves logged.</p>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied({ ...f, userId: f.userId.trim(), action: f.action.trim() });
        }}
      >
        <select className={input} value={f.staffId} onChange={(e) => setF({ ...f, staffId: e.target.value })} aria-label="Staff member">
          <option value="">All staff</option>
          {staff.data?.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
        <input className={cx(input, 'w-44')} placeholder="Action, e.g. trade_correction" value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })} aria-label="Action" />
        <input className={cx(input, 'w-72')} placeholder="Client user id" value={f.userId} onChange={(e) => setF({ ...f, userId: e.target.value })} aria-label="Client user id" />
        <TimeFrameSelect
          label="Audit period"
          zone="utc"
          presets={AUDIT_PERIODS}
          value={period}
          onChange={(p) => {
            // The time frame applies at once; the text filters wait for Filter.
            const r = periodRangeOf(p, (d) => d, 'utc');
            setPeriod(p);
            setF({ ...f, from: r?.from ?? '', to: r?.to ?? '' });
            setApplied({ ...applied, from: r?.from ?? '', to: r?.to ?? '' });
          }}
        />
        <button type="submit" className={btn}>
          Filter
        </button>
        <button type="button" className={btn} disabled={exporting} onClick={() => void exportCsv()}>
          {exporting ? <Spinner /> : 'Export CSV'}
        </button>
        {exportErr && <span className="text-xs text-down">{exportErr}</span>}
      </form>
      {error && <p className="m-0 text-xs text-down">{error}</p>}
      <Table head={['Time', 'Staff', 'Screen', 'Action', 'Target', 'Reason', 'Change']} minWidth={1000}>
        {data?.map((r) => (
          <tr key={r.id} className="border-b border-line">
            <td className={cx(td, 'whitespace-nowrap text-muted')}>{fmtDateTime(r.at)}</td>
            <td className={td}>
              {r.actor.name}
              {r.actor.role && <span className="text-muted"> · {ROLE_LABEL[r.actor.role]}</span>}
            </td>
            <td className={td}>
              <Via via={r.channel} />
            </td>
            <td className={cx(td, 'num')}>{r.action}</td>
            <td className={cx(td, 'num text-muted')}>
              {r.targetType} {r.targetId.slice(0, 8)}
            </td>
            <td className={td}>{r.reason ?? ''}</td>
            <td className={cx(td, 'num')}>
              {changeLines(r.before, r.after).map((l) => (
                <div key={l}>{l}</div>
              ))}
            </td>
          </tr>
        ))}
      </Table>
      {data && !data.length && <Empty>No audit entries{applied ? ' for this client' : ''}.</Empty>}
    </div>
  );
}

// ---------- bulk rollover adjustment ----------

/** Paste "position id, amount" lines (e.g. an index dividend across positions); one request per line. */
function BulkRollover({ onDone }: { onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [csv, setCsv] = useState('');
  const [f, setF] = useState({ reasonCode: 'dividend', evidence: '', clientNote: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const rows = csv
    .split(/\r?\n/)
    .map((l) => l.split(/[,;\t]/).map((x) => x.trim()))
    .filter((c) => c.length >= 2 && /^[0-9a-f-]{36}$/i.test(c[0]!))
    .map(([positionId, amount]) => ({ positionId: positionId!, amount: amount! }));
  const total = rows.reduce((t, r) => t + (Number(r.amount) || 0), 0);
  const submit = async () => {
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.post<{ posted: number; pending: number; failed: number; results: { ok: boolean; error?: string }[] }>('/api/admin/desk/rollover/bulk', { rows, ...f });
      const firstErr = r.results.find((x) => !x.ok)?.error;
      setMsg({ ok: !r.failed, text: `${r.posted} posted, ${r.pending} sent for approval, ${r.failed} failed${firstErr ? ` (${firstErr})` : ''}.` });
      if (!r.failed) setCsv('');
      onDone();
    } catch (e) {
      setMsg({ ok: false, text: err(e) });
    } finally {
      setBusy(false);
    }
  };
  if (!open) {
    return (
      <button type="button" className={cx(btn, 'self-start')} onClick={() => setOpen(true)}>
        Bulk rollover adjustment…
      </button>
    );
  }
  return (
    <section className="flex flex-col gap-2 rounded-xl border border-line bg-panel p-3">
      <div className="flex items-center justify-between">
        <h3 className="m-0 text-sm font-semibold">Bulk rollover adjustment</h3>
        <button type="button" className={btn} onClick={() => setOpen(false)}>
          Close
        </button>
      </div>
      <p className="m-0 text-xs text-muted">One line per position: position id, amount (+ to the client, − from them). Each line becomes its own request, or posts at once where your role allows it.</p>
      <textarea className={cx(input, 'num h-28 py-2')} placeholder={'3894335a-…, -12.50\n3894467b-…, 4.10'} value={csv} onChange={(e) => setCsv(e.target.value)} aria-label="Positions and amounts" />
      <div className="flex flex-wrap gap-2">
        <select className={input} value={f.reasonCode} onChange={(e) => setF({ ...f, reasonCode: e.target.value })} aria-label="Reason code">
          <option value="dividend">Dividend</option>
          <option value="contract_roll">Contract roll</option>
          <option value="wrong_rate">Wrong rate applied</option>
          <option value="holiday_charged">Holiday charged</option>
          <option value="other">Other</option>
        </select>
        <input className={cx(input, 'min-w-60 flex-1')} placeholder="Evidence (rate table, dividend notice)" value={f.evidence} onChange={(e) => setF({ ...f, evidence: e.target.value })} aria-label="Evidence" />
        <input className={cx(input, 'min-w-60 flex-1')} placeholder="Note on the client's statement" value={f.clientNote} onChange={(e) => setF({ ...f, clientNote: e.target.value })} aria-label="Client note" />
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <span className="num text-muted">
          {rows.length} positions · total {fmtUsd(String(total.toFixed(2)))}
        </span>
        <button type="button" className={primary} disabled={busy || !rows.length || !f.evidence.trim() || !f.clientNote.trim()} onClick={() => void submit()}>
          {busy ? <Spinner /> : 'Submit'}
        </button>
        {msg && <span className={msg.ok ? 'text-up' : 'text-down'}>{msg.text}</span>}
      </div>
    </section>
  );
}
